import { makeAutoObservable, makeObservable, observable } from 'mobx';
import { ITransform, ITransformRow, TransformIq } from '../models/ITransform';
import { IScript } from '../models/IScript';
import { getTransformFromText } from '../views/pages/project/TransformMode/getTransformFromText';
import store from '../store';
import * as scriptEditorActions from '../redux/scriptEditor/actions.scriptEditor';
import * as _ from 'lodash';
import { getEmptyTransform, getEmptyTransformRow, getTransformText } from '../views/pages/project/TransformMode/getTransformText';
import { convertTransformPropValueToString } from '../views/pages/project/TransformMode/formatValueForTransform';
import ToasterService from '../services/ToasterService';
import { guid } from '../../../alan_btn/src/helpers/mini/guid';
import { isRowEmpty } from '../views/pages/project/TransformMode/transformHelper';
const Fuse = require("fuse.js")

type SearchTransfromFilter = {
    input?: string,
    query?: string,
    prompt?: string,
    result?: string,
};
class Transforms {

    wasInitialized: boolean = false;
    rowToCopy: ITransformRow = null;
    linesFilter: number[] = []
    script: IScript;
    rowsStreams: Record<string, string>;
    rowsStreamsUpdateMode: Record<string, 'append' | 'replace'>;
    rowsStreamsHistory: Record<string, string>;
    rowsStreamsFinishCb: Record<string, () => {}>;
    searchFilters: SearchTransfromFilter = {};
    configTouched: boolean = false;
    transform: ITransform = {
        rows: [],
        header: {
            input: { format: 'text' },
            query: { format: 'text' },
            prompt: { format: 'text', value: '' },
            result: { format: 'text' },
        },
        config: getEmptyTransform().config
    } as ITransform;

    historyTransform: ITransform = {
        rows: [],
        header: {
            input: { format: 'text' },
            query: { format: 'text' },
            prompt: { format: 'text', value: '' },
            result: { format: 'text' },
        },
        config: getEmptyTransform().config
    } as ITransform;


    constructor() {
        makeAutoObservable(this);
    }

    init(script: IScript, scriptDraft: string) {
        this.script = script;
        this.transform = getTransformFromText(scriptDraft);
        this.rowsStreams = {};
        this.rowsStreamsHistory = {};
        this.rowsStreamsFinishCb = {};
        this.rowsStreamsUpdateMode = {};
        this.searchFilters = {};
        this.configTouched = false;
        if (this.linesFilter?.length) {
            for (let i = 0; i < this.transform?.rows.length; i++) {
                this.transform.rows[i].visible = this.linesFilter.includes(i);
            }
        }
        if (this.rowToCopy) {
            this.duplicateRow({
                ...this.rowToCopy,
                id: guid(),
                visible: true,
                editing: true,
                rowIndex: this.transform.rows?.length
            });

            this.dispatchChanges();
        }
        this.wasInitialized = true;
    }

    resetStore() {
        this.wasInitialized = false;
        this.rowsStreams = {};
        this.rowsStreamsHistory = {};
        this.rowsStreamsFinishCb = {};
        this.rowsStreamsUpdateMode = {};
        this.rowToCopy = null;
        this.script = null;
        this.searchFilters = {};
        this.configTouched = false;
        this.resetLineFilters();
    }

    resetRowToCopy() {
        this.rowToCopy = null;
    }

    initHistoryTransform(transform: ITransform) {
        this.historyTransform = transform;
    }

    resetHistoryTransform() {
        this.historyTransform = null;
    }

    updateTransformAfterSaving() {
        if (!this.wasInitialized) return;

        this.transform.rows = observable(this.transform.rows.filter(row => !isRowEmpty(row) && !row.deleting));

        if (this.transform.rows?.length === 0) {
            this.transform.rows.push(getEmptyTransformRow(0))
        }

        for (let i = 0; i < this.transform.rows.length; i++) {
            this.transform.rows[i].isNew = false;
            this.transform.rows[i].rowIndex = i;
        }
        this.script.text = getTransformText(this.transform, true);
    }

    discardChangesInTransform(script: IScript) {
        const transform = getTransformFromText(script.text);
        this.transform.rows = observable(transform.rows);
        this.transform.header = observable(transform.header);
        this.transform.config = observable(transform.config);
        this.configTouched = false;
    }

    setLineFilters(lines) {
        this.linesFilter = observable(lines);
    }

    resetLineFilters() {
        this.linesFilter = observable([]);
        for (let i = 0; i < this.transform.rows.length; i++) {
            this.transform.rows[i].visible = true;
        }
    }

    updateIq(iq: TransformIq) {
        this.transform.config.iq = iq;
        this.dispatchChanges();
    }

    updateTemperature(temp: number) {
        this.transform.config.temperature = temp;
        this.dispatchChanges();
    }

    updateMaxTokens(maxTokens: number) {
        this.transform.config.maxTokens = maxTokens;
        this.dispatchChanges();
    }

    touchConfig = () => {
        this.configTouched = true;
    }

    discardConfigChanges() {
        const savedTransform = getTransformFromText(this.script.text);
        const savedTransformConfig = savedTransform.config;
        this.transform.config.maxTokens = savedTransformConfig.maxTokens;
        this.transform.config.iq = savedTransformConfig.iq;
        this.transform.config.temperature = savedTransformConfig.temperature;
        this.configTouched = false;
        this.dispatchChanges();
    }

    updateFormat(prop: string, value: string) {
        this.transform.header[prop].format = value;
        this.dispatchChanges();
    }

    updateMainPrompt(value: string) {
        this.transform.header.prompt.value = value;
        this.dispatchChanges();
    }

    setRowToCopy(row: ITransformRow) {
        if (this.wasInitialized) {
            this.duplicateRow(row);
        } else {
            this.rowToCopy = row;
        }
    }

    addRow() {
        this.transform.rows.push(getEmptyTransformRow(this.transform.rows.length));
        console.info('this.transform.rows', this.transform.rows);
        this.dispatchChanges();
    }

    duplicateRow(row: ITransformRow) {

        if (this.transform.rows?.length > 0) {
            const isLastEmpty = isRowEmpty(this.transform.rows[this.transform.rows.length - 1]);
            if (isLastEmpty) {
                this.transform.rows.pop();
            }
        }

        this.transform.rows.push({
            ...row,
            id: guid(),
            isNew: true,
            rowIndex: this.transform.rows?.length || 1,
            visible: true
        });
        this.dispatchChanges();
    }

    updateRow(name: string, value: string, rowId: string) {
        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                this.transform.rows[i][name] = value;
                break;
            }
        }

        this.dispatchChanges();
    }

    markRowToDelete(rowId: string) {
        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                this.transform.rows[i].deleting = true;
                this.transform.rows[i].input = '';
                this.transform.rows[i].query = '';
                this.transform.rows[i].prompt = '';
                this.transform.rows[i].result = '';
                break;
            }
        }
        this.dispatchChanges();
    }

    discardRowChange(rowId: string, initRow: ITransformRow) {
        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                this.transform.rows[i].input = initRow.input;
                this.transform.rows[i].query = initRow.query;
                this.transform.rows[i].prompt = initRow.prompt;
                this.transform.rows[i].result = initRow.result;
                this.transform.rows[i].deleting = undefined;
                break;
            }
        }
        this.dispatchChanges();
    }

    search(prop: string, value: string, previewMode: boolean) {
        let foundRowsByInput = (previewMode ? this.historyTransform?.rows : this.transform?.rows) || [];
        let foundRowsByQuery = (previewMode ? this.historyTransform?.rows : this.transform?.rows) || [];
        let foundRowsByPrompt = (previewMode ? this.historyTransform?.rows : this.transform?.rows) || [];
        let foundRowsByResult = (previewMode ? this.historyTransform?.rows : this.transform?.rows) || [];

        this.searchFilters[prop] = value;

        if (this.searchFilters?.input) {
            foundRowsByInput = searchWithFuse(foundRowsByInput, "input", this.searchFilters?.input || '');
        }

        if (this.searchFilters?.query) {
            foundRowsByQuery = searchWithFuse(foundRowsByQuery, "query", this.searchFilters?.query || '');
        }

        if (this.searchFilters?.prompt) {
            foundRowsByPrompt = searchWithFuse(foundRowsByPrompt, "prompt", this.searchFilters?.prompt || '');
        }

        if (this.searchFilters?.result) {
            foundRowsByResult = searchWithFuse(foundRowsByResult, "result", this.searchFilters?.result || '');
        }

        const resRows = _.intersectionBy(foundRowsByInput, foundRowsByQuery, foundRowsByPrompt, foundRowsByResult, 'id');
        const resRowsIds = resRows.map(r => r.id);

        if (previewMode) {
            for (let i = 0; i < this.historyTransform.rows.length; i++) {
                this.historyTransform.rows[i].visible = resRowsIds.indexOf(this.historyTransform.rows[i].id) > -1;
            }
        } else {
            for (let i = 0; i < this.transform.rows.length; i++) {
                this.transform.rows[i].visible = resRowsIds.indexOf(this.transform.rows[i].id) > -1;
            }
        }
    }

    updateResultWithGenerationResponse(text: string, rowId: string) {
        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                const updateMode = this.rowsStreamsUpdateMode[rowId];

                this.updateRow('result',
                    updateMode === 'append' ?
                        (convertTransformPropValueToString(this.transform.rows[i].result) + text) :
                        text,
                    rowId);

                if (updateMode === 'replace') {
                    this.rowsStreamsUpdateMode[rowId] = 'append';
                }
                break;
            }
        }
    }

    completeResultGeneration(rowId: string) {
        if (this.rowsStreamsFinishCb[rowId]) {
            this.rowsStreamsFinishCb[rowId]();
        }

        delete this.rowsStreams[rowId];
        delete this.rowsStreamsFinishCb[rowId];
        delete this.rowsStreamsUpdateMode[rowId];

        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                this.transform.rows[i].isResultLoading = false;
                break;
            }
        }
    }

    dispatchChanges() {
        const newScriptText = getTransformText(this.transform, false);
        store.dispatch(scriptEditorActions.updateScriptTextInEditor({ script: this.script, newScriptText }));
    }

    stopResultGeneration(rowId: string) {
        console.info('stopResultGeneration', rowId);
        this.completeResultGeneration(rowId);
    }

    hasValueToRevertResultBeforeGeneration(rowId: string) {
        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                if (this.rowsStreamsHistory[rowId] !== undefined) {
                    return true;
                }
            }
        }
        return false;
    }


    revertResultBeforeGeneration(rowId: string) {
        console.info('revertResultBeforeGeneration', rowId);

        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === rowId) {
                if (this.rowsStreamsHistory[rowId] !== undefined) {
                    this.transform.rows[i].result = this.rowsStreamsHistory[rowId];
                    delete this.rowsStreamsHistory[rowId];
                }
                break;
            }
        }
        this.dispatchChanges();
    }

    isChangesNeedToBeSavedBeforeGeneratingResult(row: ITransformRow, index: number, unsavedFiles) {
        if (Object.keys(unsavedFiles).length > 1) {
            return true;
        }
        if (unsavedFiles[this.script.name]) {
            const transformWithoutCurrentText = getTransformText({
                ...this.transform,
                rows: this.transform?.rows?.filter(el => el.id !== row.id)
            }, true);

            const savedTransform = getTransformFromText(this.script.text);
            savedTransform.rows = savedTransform?.rows?.filter((el, i) => index !== i);
            const savedTransformText = getTransformText(savedTransform, true);

            return transformWithoutCurrentText !== savedTransformText;
        }
        return false;
    }

    generateResult(row: ITransformRow, index: number, onFinishCb) {
        console.info('generateResult start')

        this.rowsStreams[row.id] = row.id;

        for (let i = 0; i < this.transform.rows.length; i++) {
            if (this.transform.rows[i].id === row.id) {
                this.transform.rows[i].isResultLoading = true;
                break;
            }
        }

        window.tutorProject.call('transform', {
            name: this.script.name,
            input: convertTransformPropValueToString(row.input), //todo: use better fix
            query: convertTransformPropValueToString(row.query),
            prompt: convertTransformPropValueToString(row.prompt),
            result: convertTransformPropValueToString(row.result),
            generateColumn: name,
            index: index
        }, (e, res) => {
            if (res && res.streamId !== undefined) {
                if (res.streamId) {
                    this.rowsStreams[row.id] = res.streamId;
                    this.rowsStreamsHistory[row.id] = row.result;
                    this.rowsStreamsFinishCb[row.id] = onFinishCb;
                    this.rowsStreamsUpdateMode[row.id] = res.updateMode || 'append';
                }
            } else {
                this.stopResultGeneration(row.id);
                ToasterService.showError({ title: 'Unable to generate result, please try later' });
                console.error(e);
            }
        });
    }
}

function generateResultStreamListener(e: CustomEvent) {
    if (e && e.type === `streamContentUpdate`) {
        const { streamId, final, text } = e.detail || {};

        const rowStream = _.find(Object.keys(transformsStore.rowsStreams)
            .map(key => ({ rowId: key, streamId: transformsStore.rowsStreams[key] })) as { rowId: string, streamId: string }[],
            { streamId }) as { rowId: string, streamId: string };

        if (rowStream) {
            if (final !== true) {
                transformsStore.updateResultWithGenerationResponse(text, rowStream.rowId);
            } else {
                transformsStore.completeResultGeneration(rowStream.rowId);
            }
        }
    }
}

window.addEventListener(`streamContentUpdate`, generateResultStreamListener, false);

if (!window.alanStores?.transformsStore) {
    if (!window.alanStores) {
        window.alanStores = {};
    }
    window.alanStores.transformsStore = new Transforms();
}

const debouncedSearchLog = _.debounce((key, results) => {
    console.info(`Search by: ${key}`, results);
}, 2000);

function searchWithFuse(rows, key, searchVal) {
    const fuseInput = new Fuse(rows, {
        isCaseSensitive: false,
        ignoreLocation: true,
        ignoreFieldNorm: true,
        shouldSort: false,
        threshold: 0.24,
        includeScore: true,
        keys: [key]
    });

    debouncedSearchLog(key, fuseInput.search(searchVal));

    return fuseInput.search(searchVal).map(el => el.item);
}

export const transformsStore = window.alanStores.transformsStore as Transforms;